import { ApiQueryParams } from '@frontend/api-utils';
import { Functionality } from '@frontend/functionality/types';
import { SettingKey, SettingValue } from '@frontend/setting/types';
import { TransactionUserClient } from '@frontend/transaction/api';
import { TransactionUser, TransactionUserType } from '@frontend/transaction/types';
import { IUser, UserListResponse, UserQueryParams } from '@frontend/user/types';
import { User } from '@frontend/user/utils';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { resolveUsersByGroupMemberOrder } from '../../utils/resolve-users-by-group-member-order';
import { useScreen } from '../useScreen';

const toBooleanSetting = (value: SettingValue | undefined): boolean => value === true || value === 'true' || value === 1 || value === '1';

const DELAYED_RECEIVERS_MIN_SEARCH_CHARACTERS = 3;

interface ViewProps {
    functionality?: Functionality;
    isLoading?: boolean;
    receivers?: IUser[] | null;
    selectedReceivers: (string | null)[];
    receiverSubsets: (string[] | undefined)[];
    delayedReceiversSearch: boolean;
    showUnknownReceiver: boolean;
    fetchReceiverOptions: (queryParams?: ApiQueryParams<UserQueryParams>) => Promise<UserListResponse>;
    addReceiver: () => void;
    removeReceiver: (index: number) => void;
    updateReceiver: (index: number, receiver: string | null) => void;
    canContinue: boolean;
    onSubmit: () => void;
    onUnknownReceiver: () => void;
    onBack: (screenType: ScreenType) => void;
}

const useSelectReceivers = (): ViewProps => {
    const { store, onBack, onSubmit: onContinue, updateWizardData } = useScreen();
    const { transaction } = useWizardTransaction({
        wizardId: store?.wizard.id,
        store,
        autoCreate: true
    });
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [receivers, setReceivers] = useState<IUser[] | null>(null);
    const [selectedReceivers, setSelectedReceivers] = useState<(string | null)[]>([null]);
    const selectedReceiversRef = useRef(selectedReceivers);

    const storeReceivers = (store?.data?.receivers as TransactionUser[]) || [];
    const remoteReceiversRef = useRef<TransactionUser[]>([]);
    const hasAutoContinuedRef = useRef(false);

    const delayedReceiversSearch = toBooleanSetting(store?.functionalitySettings?.find((fs) => fs.key === SettingKey.DELAYED_RECEIVERS_SEARCH)?.value);

    const defaultReceiverIdValue = store?.functionalitySettings?.find((fs) => fs.key === SettingKey.DEFAULT_RECEIVER_ID)?.value;
    const defaultReceiverId = typeof defaultReceiverIdValue === 'string' && defaultReceiverIdValue.trim() ? defaultReceiverIdValue.trim() : null;

    useEffect(() => {
        selectedReceiversRef.current = selectedReceivers;
    }, [selectedReceivers]);

    const writeReceivers = (receiverIds: string[]) => {
        if (!store) return;

        const newReceivers: Partial<TransactionUser>[] = receiverIds.map((userId) => ({
            user_id: userId,
            type: TransactionUserType.RECEIVER,
            transaction_id: store.data?.transaction?.id || '',
            account_id: store.wizard.account_id,
            creation_timestamp: new Date().toISOString(),
            update_timestamp: new Date().toISOString()
        }));

        updateWizardData({
            ...store.data,
            receivers: newReceivers
        });
    };

    const syncReceiversAndAdvance = async (receiverIds: string[]) => {
        if (!store || !transaction?.id) return;

        const accountId = store.wizard.account_id;
        const transactionId = transaction.id;
        const remoteReceivers = remoteReceiversRef.current;

        const receiversToAdd = receiverIds.filter((local) => !remoteReceivers.find((remote) => remote.user_id === local));
        const receiversToRemove = remoteReceivers.filter((remote) => !receiverIds.find((local) => local === remote.user_id));

        const addedReceivers: TransactionUser[] = [];
        for (const receiver of receiversToAdd) {
            const addedReceiver = await TransactionUserClient.postTransactionUser(accountId, transactionId, {
                user_id: receiver,
                type: TransactionUserType.RECEIVER
            });
            addedReceivers.push(addedReceiver);
        }

        for (const receiver of receiversToRemove) {
            await TransactionUserClient.deleteTransactionUser(accountId, transactionId, receiver.user_id);
        }

        remoteReceiversRef.current = [
            ...addedReceivers,
            ...storeReceivers.filter((local) => remoteReceivers.find((remote) => remote.user_id === local.user_id))
        ] as TransactionUser[];

        const nextStep = store.steps.find((step) => step.screen === ScreenType.SELECT_RECEIVERS && step.trigger === ScreenTrigger.SUBMIT);
        onContinue(nextStep);
    };

    useEffect(() => {
        const receiverUserGroupId = store?.functionalitySettings?.find((fs) => fs.key === SettingKey.OPEN_DROP_OFF_RECEIVERS_USER_GROUP_ID)?.value;

        if (receiverUserGroupId && typeof receiverUserGroupId === 'string') {
            const getInfo = async () => {
                const orderedReceivers = await resolveUsersByGroupMemberOrder(receiverUserGroupId);
                setReceivers(orderedReceivers);

                if (transaction?.id) {
                    const fetchedReceivers = await TransactionUserClient.fetchTransactionUsers({
                        transaction_id: transaction.id,
                        type: TransactionUserType.RECEIVER
                    });
                    remoteReceiversRef.current = fetchedReceivers.results;

                    if (storeReceivers.length === 0 && fetchedReceivers.results.length > 0) {
                        updateWizardData({
                            ...store.data,
                            receivers: fetchedReceivers.results
                        });
                    }
                }

                const foundReceivers = orderedReceivers.filter((u) => storeReceivers.find((r) => r.user_id === u.id));
                if (foundReceivers.length > 0) {
                    setSelectedReceivers(foundReceivers.map((r) => r.id));
                }

                if (orderedReceivers.length !== 1 || !transaction?.id || hasAutoContinuedRef.current) {
                    return;
                }

                const singleReceiverId = orderedReceivers[0].id;
                hasAutoContinuedRef.current = true;
                try {
                    setSelectedReceivers([singleReceiverId]);
                    writeReceivers([singleReceiverId]);
                    await syncReceiversAndAdvance([singleReceiverId]);
                } catch {
                    hasAutoContinuedRef.current = false;
                }
            };
            getInfo().finally(() => {
                if (!hasAutoContinuedRef.current) {
                    setIsLoading(false);
                }
            });
        } else {
            setIsLoading(false);
        }
    }, []);

    const addReceiver = () => {
        setSelectedReceivers([...selectedReceivers, null]);
    };

    const removeReceiver = (index: number) => {
        setSelectedReceivers(selectedReceiversRef.current.filter((_, i) => i !== index));
    };

    const updateReceiver = (index: number, receiver: string | null) => {
        const updated = [...selectedReceivers];
        updated[index] = receiver;
        setSelectedReceivers(updated);
        writeReceivers(updated.filter((r): r is string => r !== null));
    };

    const receiverSubsets = useMemo(
        () =>
            selectedReceivers.map((receiverId) =>
                receivers
                    ?.filter((receiver) => {
                        const found = selectedReceivers.find((id) => id === receiver.id);
                        if (found) {
                            return receiverId === found;
                        }
                        return true;
                    })
                    .map((receiver) => receiver.id)
            ),
        [receivers, selectedReceivers]
    );

    const fetchReceiverOptions = useCallback(
        (queryParams?: ApiQueryParams<UserQueryParams>): Promise<UserListResponse> => {
            const search = typeof queryParams?.search === 'string' ? queryParams.search.trim().toLowerCase() : '';

            if (delayedReceiversSearch && search.length < DELAYED_RECEIVERS_MIN_SEARCH_CHARACTERS) {
                return Promise.resolve({ count: 0, results: [] });
            }

            let filtered = receivers ?? [];

            if (search) {
                filtered = filtered.filter((user) => {
                    const displayName = new User(user).getDisplayName().toLowerCase();
                    return displayName.includes(search) || user.unique_identifier.toLowerCase().includes(search);
                });
            }

            const index = Number.parseInt(String(queryParams?.index ?? '0'), 10);
            const size = Number.parseInt(String(queryParams?.size ?? '100'), 10);

            return Promise.resolve({
                count: filtered.length,
                results: filtered.slice(index * size, (index + 1) * size)
            });
        },
        [receivers, delayedReceiversSearch]
    );

    const canContinue = selectedReceivers.some((r) => r !== null) && selectedReceivers.length > 0;
    const showUnknownReceiver = !!defaultReceiverId && !canContinue;

    const onSubmit = async () => {
        if (!store || !canContinue || !transaction?.id) return;

        setIsLoading(true);
        try {
            const relevantReceivers = selectedReceiversRef.current.filter((r): r is string => r !== null);
            await syncReceiversAndAdvance(relevantReceivers);
        } finally {
            setIsLoading(false);
        }
    };

    const onUnknownReceiver = async () => {
        if (!defaultReceiverId || !transaction?.id) return;

        setIsLoading(true);
        try {
            const nextSelection = [defaultReceiverId];
            setSelectedReceivers(nextSelection);
            selectedReceiversRef.current = nextSelection;
            writeReceivers(nextSelection);
            await syncReceiversAndAdvance(nextSelection);
        } finally {
            setIsLoading(false);
        }
    };

    return {
        functionality: store?.functionality,
        isLoading,
        receivers,
        selectedReceivers,
        receiverSubsets,
        delayedReceiversSearch,
        showUnknownReceiver,
        fetchReceiverOptions,
        addReceiver,
        removeReceiver,
        updateReceiver,
        canContinue,
        onSubmit,
        onUnknownReceiver,
        onBack
    };
};

export default useSelectReceivers;
